<template>
  <Modal :title="title" name="APPROVE_REFUSE_INVOICE_MODAL" actions>
     <template v-slot:body>
      <p>{{ warningMessage }}</p>

      <v-card color="grey lighten-4" class="pa-5 mb-8" elevation="0">
        <h6 class="text-uppercase grey--text">{{ $t('hausefulInvoices.customer') }}</h6>
        <p class="body-2 mb-2">{{ customer }}</p>
        <h6 class="text-uppercase grey--text">{{ $t('hausefulInvoices.host') }}</h6>
        <p class="body-2 mb-2">{{ host }}</p>
        <h6 class="text-uppercase grey--text">{{ $t('hausefulInvoices.total') }}</h6>
        <p class="body-2 mb-0">{{ total }}</p>
      </v-card>

      <v-form v-model="valid" ref="refuseForm">
        <div v-if="isRefuse">
          <v-select
            v-model="selectedOption"
            :items="commentOptions"
            :rules="[rules.required]"
            :label="$t('filters.select')"
            return-object
            outlined
          />
          <v-textarea
            v-if="showComment"
            v-model="comment"
            :label="$t('hausefulInvoices.actions.comment')"
            :rules="[rules.required]"
            outlined
          />
        </div>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        text
      >
        {{ $t('hausefulInvoices.actions.cancel') }}
      </v-btn>
      <v-btn
        @click="approve"
        :loading="loading"
        class="ml-5 primary"
        elevation="0"
      >
        {{ buttonLabel }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { catalogMonetary } from '@/util/catalog'
import { required } from '@/util/errorHandling'

import Modal from '@/components/app/Modal'

export default {
  name: 'ModalApproveRefuseInvoice',

  components: {
    Modal
  },

  watch: {
    invoiceApprove(isApproved) {
      if (isApproved) {
        this.isForApprovement = true
        this.title = this.$t('hausefulInvoices.modals.approve.title')
        this.buttonLabel = this.$t('hausefulInvoices.actions.approve')
        this.warningMessage = this.$t('hausefulInvoices.modals.approve.message')
      } else {
        this.isForApprovement = false
        this.title = this.$t('hausefulInvoices.modals.refuse.title')
        this.buttonLabel = this.$t('hausefulInvoices.actions.refuse')
        this.warningMessage = this.$t('hausefulInvoices.modals.refuse.message')
      }
    }
  },

  computed: {
    ...mapGetters(['adminInvoice', 'invoiceApprove']),

    host() {
      return this.adminInvoice.host?.name ?? '---'
    },

    customer() {
      return this.adminInvoice.real_estate?.name ?? '---'
    },

    total() {
      return this.adminInvoice.total ? catalogMonetary(this.adminInvoice.total) : '---'
    },

    isRefuse() {
      return !this.invoiceApprove
    },

    showComment() {
      return this.selectedOption?.value === 'other'
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions(['approveInvoices']),

    approve() {
      if (!this.$refs.refuseForm.validate()) return

      this.loading = true
      let comment = null

      if (!this.invoiceApprove) {
        comment = this.selectedOption?.value === 'other' ? this.comment : this.selectedOption.text
      }

      const payload = {
        invoiceId: this.adminInvoice.id,
        approved: this.invoiceApprove,
        comment,
      }

      this.approveInvoices(payload).finally(() => {
        this.loading = false
        this.comment = null
      })
    }
  },

  data() {
    return {
      valid: false,
      comment: null,
      loading: false,
      isForApprovement: false,
      rules: { required },

      catalogMonetary,

      title: this.invoiceApprove ? this.$t('hausefulInvoices.modals.approve.title') : this.$t('hausefulInvoices.modals.refuse.title'),
      buttonLabel: this.invoiceApprove ? this.$t('hausefulInvoices.actions.approve') : this.$t('hausefulInvoices.actions.refuse'),
      warningMessage: this.invoiceApprove ? this.$t('hausefulInvoices.modals.approve.message') : this.$t('hausefulInvoices.modals.refuse.message'),

      selectedOption: null,
      commentOptions: [
        {
          text: this.$t('hausefulInvoices.commentOptions.noValue'),
          value: 'noValue',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.wrongValue'),
          value: 'wrongValue',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.noDocument'),
          value: 'noDocument',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.wrongDocument'),
          value: 'wrongDocument',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.wrongCNPJ'),
          value: 'wrongCNPJ',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.wrongDescription'),
          value: 'wrongDescription',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.noInvoice'),
          value: 'noInvoice',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.noServiceTaker'),
          value: 'noServiceTaker',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.wrongServiceTaker'),
          value: 'wrongServiceTaker',
        },
        {
          text: this.$t('hausefulInvoices.commentOptions.other'),
          value: 'other',
        },
      ],
    }
  },
}
</script>
